import { select, call, takeLatest, put } from 'redux-saga/effects';

import { ProviderReportActions } from '../constants';
import { ProviderReportApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteProviderReportAction,
    GetProviderReportAction,
    GetProviderReportsAction,
} from '../actions/providerReport-actions';
import sortingService from '../../services/sorting';
import { handleError, resolveApiCall, ResolverApiFailure, successType } from '../../services/api-handlers/api-resolver';
import { groupProviderReportToCsv } from '../../helpers/providerReport/groupCsv';
import { groupProviderReportToTotal } from '../../helpers/providerReport/groupTotal';
import { exportToCsv } from '../../services/exports/csv';
import { toast } from 'react-toastify';

function* handleFetchProviderReports(action: GetProviderReportsAction) {
    const oldData = yield select((state: StoreState) => state.providerReports.data);
    const { filters, sorting, page, itemsPerPage, notTotal } = action.payload;

    if (filters?.caseType && filters.caseType === 'all') {
        delete filters.caseType;
    }
    if (!notTotal) {
        const actionFiles: GetProviderReportsAction = {
            type: ProviderReportActions.FETCH_PROVIDER_REPORTS_TOTAL,
            payload: {
                filters,
                page: 1,
            },
        };
        yield call(handleFetchProviderReportsTotal, actionFiles);
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        ProviderReportApi.getProviderReports({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page: page ? page : 1,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}
function* handleFetchProviderReportsCsv(action: GetProviderReportsAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    yield resolveApiCall(
        action,
        () =>
            ProviderReportApi.getProviderReports({
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 5000) {
                const actionFiles: GetProviderReportsAction = {
                    type: ProviderReportActions.FETCH_PROVIDER_REPORTS_CSV,
                    payload: {
                        ...filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 5000,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchProviderReportsCsv, actionFiles);
            } else {
                const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];
                if (action.all) {
                    const groupCsv = groupProviderReportToCsv(dataAll);
                    exportToCsv('provider-report.csv', groupCsv);
                } else {
                    const groupCsvData = groupProviderReportToCsv(data.payload);
                    exportToCsv('provider-report.csv', groupCsvData);
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}
function* handleFetchProviderReportsTotal(action: GetProviderReportsAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    yield resolveApiCall(
        action,
        () =>
            ProviderReportApi.getProviderReports({
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 80) {
                const actionFiles: GetProviderReportsAction = {
                    type: ProviderReportActions.FETCH_PROVIDER_REPORTS_TOTAL,
                    payload: {
                        ...filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 80,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchProviderReportsTotal, actionFiles);
            } else {
                if (action.all) {
                    const groupData = groupProviderReportToTotal(action.all);
                    yield put({
                        type: successType(action.type),
                        payload: groupData,
                        total: true,
                    });
                } else {
                    const groupData = groupProviderReportToTotal(data.payload);
                    yield put({
                        type: successType(action.type),
                        payload: groupData,
                        total: true,
                    });
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchProviderReport(action: GetProviderReportAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => ProviderReportApi.getProviderReports(id));
}

function* handleDeleteProviderReports(action: DeleteProviderReportAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => ProviderReportApi.deleteProviderReport(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(ProviderReportActions.FETCH_PROVIDER_REPORTS, handleFetchProviderReports);
    yield takeLatest(ProviderReportActions.FETCH_PROVIDER_REPORTS_CSV, handleFetchProviderReportsCsv);
    yield takeLatest(ProviderReportActions.FETCH_PROVIDER_REPORTS_TOTAL, handleFetchProviderReportsTotal);
    yield takeLatest(ProviderReportActions.FETCH_PROVIDER_REPORT, handleFetchProviderReport);
    yield takeLatest(ProviderReportActions.DELETE_PROVIDER_REPORT, handleDeleteProviderReports);
}
